import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';

/**
 * Confirmação que some sozinha.
 *
 * Duas decisões que não são detalhe:
 *
 * **Vai para o `document.body`, por portal.** Os cards do quadro vivem dentro
 * de um `SortableSection`, e o dnd-kit aplica `transform` neles. Um ancestral
 * com `transform` vira o bloco de contenção de qualquer `position: fixed`
 * dentro dele — o toast ficaria preso ao canto do card, não ao da tela, e
 * mudaria de lugar conforme o card fosse arrastado.
 *
 * **O callback vive num ref.** O pai quase sempre passa `() => setX(null)`
 * inline, que tem identidade nova a cada render. Com ele nas dependências do
 * efeito, qualquer render do pai reiniciaria o cronômetro — e num card que
 * recarrega sozinho o toast poderia não sumir nunca.
 */

interface Props {
  /** `null` esconde. Trocar o texto reinicia o tempo. */
  message: string | null;
  onDismiss: () => void;
  /** Milissegundos até sumir. */
  duration?: number;
}

const Toast: React.FC<Props> = ({ message, onDismiss, duration = 4000 }) => {
  const dispensar = useRef(onDismiss);
  dispensar.current = onDismiss;

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => dispensar.current(), duration);
    return () => clearTimeout(t);
  }, [message, duration]);

  if (!message) return null;

  return createPortal(
    // `status` + `polite`: o leitor de tela anuncia quando terminar a frase
    // atual, sem interromper. Confirmação não é alerta.
    <div className="toast" role="status" aria-live="polite">
      <span className="toast-check" aria-hidden="true">✓</span>
      <span className="toast-msg">{message}</span>
      <button className="toast-fechar" onClick={() => dispensar.current()} aria-label="Fechar aviso">
        ✕
      </button>
    </div>,
    document.body,
  );
};

export default Toast;
